import React, { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { useSearchParams } from 'react-router-dom';
import {
  CaretLeft,
  CaretRight,
  CheckCircle,
  Clock,
  CurrencyDollar,
  FunnelSimple,
  MagnifyingGlass,
  Receipt,
  WarningCircle,
  X,
  XCircle,
} from 'phosphor-react';

import { GlassCard, GlassPageBg } from '../../../../components/glass';
import {
  ILancamentoFinanceiro,
  TipoLancamento,
  useFinanceiroFacets,
  useFinanceiroKpis,
  useFinanceiroLancamentos,
} from '../../../../hooks/useFinanceiro';
import { FinanceiroPageHeader } from './FinanceiroPageHeader';
import { brl, fmtCompetencia, fmtData, isVencido } from './format';
import { InfoPopover } from './InfoPopover';
import { KpiCard } from './KpiCard';
import { Segmented } from './Segmented';
import { StatusBadge } from './StatusBadge';
import { useFinanceiroFilters } from './useFinanceiroFilters';

const HEADER = {
  RECEITA: {
    eyebrow: 'Recebíveis',
    cor: '#16A34A',
    icon: <CurrencyDollar size={24} weight="fill" />,
  },
  DESPESA: { eyebrow: 'Despesas', cor: '#DC2626', icon: <Receipt size={24} weight="fill" /> },
} as const;

const PAGE_SIZE = 20;

interface LancamentosViewProps {
  tipo: TipoLancamento;
}

const TEXTOS = {
  RECEITA: {
    titulo: 'Contas a Receber',
    subtitulo: 'Recebíveis extraídos do Conta Azul',
    pessoa: 'Cliente',
    pago: 'Recebido',
    totalKpi: 'Faturado',
  },
  DESPESA: {
    titulo: 'Contas a Pagar',
    subtitulo: 'Despesas extraídas do Conta Azul',
    pessoa: 'Fornecedor',
    pago: 'Pago',
    totalKpi: 'Total',
  },
} as const;

// Mapa de status_traduzido (cru do Conta Azul) -> rótulo/cor para as pills.
const STATUS_META: Record<string, { label: (t: TipoLancamento) => string; cor: string }> = {
  RECEBIDO: { label: t => (t === 'RECEITA' ? 'Recebido' : 'Pago'), cor: '#16A34A' },
  RECEBIDO_PARCIAL: { label: () => 'Parcial', cor: '#0EA5E9' },
  EM_ABERTO: { label: () => 'Em aberto', cor: '#1B88F5' },
  ATRASADO: { label: () => 'Vencido', cor: '#D97706' },
  PERDIDO: { label: () => 'Perdido', cor: '#B91C1C' },
};
const STATUS_ORDER = ['RECEBIDO', 'EM_ABERTO', 'ATRASADO', 'RECEBIDO_PARCIAL', 'PERDIDO'];

const diasAtraso = (iso: string | null) => {
  if (!iso) return 0;
  const [y, m, d] = iso.split('-').map(Number);
  const venc = new Date(y, (m || 1) - 1, d || 1);
  const hoje = new Date();
  const ms = hoje.setHours(0, 0, 0, 0) - venc.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor(ms / 86400000));
};

/** Campo de busca com debounce, espelhado no `q` global (URL). */
const BuscaInput = ({
  value,
  onChange,
  placeholder,
  accent,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  accent: string;
}) => {
  const [local, setLocal] = useState(value);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  valueRef.current = value;
  onChangeRef.current = onChange;

  useEffect(() => {
    setLocal(value);
  }, [value]);

  useEffect(() => {
    const id = setTimeout(() => {
      if (local !== valueRef.current) onChangeRef.current(local);
    }, 350);
    return () => clearTimeout(id);
  }, [local]);

  return (
    <InputGroup size="sm" flex="1" minW="220px" maxW={{ base: 'full', md: '360px' }}>
      <InputLeftElement pointerEvents="none">
        <MagnifyingGlass size={15} color="#94A3B8" />
      </InputLeftElement>
      <Input
        value={local}
        onChange={e => setLocal(e.target.value)}
        placeholder={placeholder}
        borderRadius="lg"
        bg="white"
        borderColor="rgba(15,23,42,0.1)"
        _focusVisible={{ borderColor: accent, boxShadow: `0 0 0 1px ${accent}` }}
      />
      {local && (
        <InputRightElement cursor="pointer" onClick={() => setLocal('')}>
          <X size={13} color="#94A3B8" />
        </InputRightElement>
      )}
    </InputGroup>
  );
};

type Opt = { value: string; label: string };

export const LancamentosView = ({ tipo }: LancamentosViewProps) => {
  const t = TEXTOS[tipo];
  const acc = HEADER[tipo].cor;

  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get('page') ?? 1));
  const f = useFinanceiroFilters();

  const [status, setStatus] = useState<string | undefined>(undefined);
  const [categoria, setCategoria] = useState<string | undefined>(undefined);
  const [centro, setCentro] = useState<string | undefined>(undefined);
  const [base, setBase] = useState<'vencimento' | 'competencia' | 'caixa'>('vencimento');

  const { data: facets } = useFinanceiroFacets(tipo, f.apiParams.conta_origem);
  const catOptions: Opt[] = (facets?.categorias ?? []).map(c => ({ value: c, label: c }));
  const centroOptions: Opt[] = (facets?.centros_custo ?? []).map(c => ({ value: c, label: c }));
  const statusPills = STATUS_ORDER.filter(s => (facets?.status ?? []).includes(s));

  const resetPage = () => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  };

  const irParaPagina = (p: number) =>
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('page', String(Math.max(1, p)));
        return next;
      },
      { replace: true },
    );

  // Período, franquia e busca (q) vêm da topbar global (URL). Os filtros
  // específicos (status/categoria/centro) e a base ficam locais à tela.
  const baseParams = {
    tipo,
    base,
    date_start: f.dateStart,
    date_end: f.dateEnd,
    conta_origem: f.apiParams.conta_origem,
    q: f.q || undefined,
    categoria,
    centro_custo: centro,
  };

  const { data: kpis } = useFinanceiroKpis(baseParams);
  const { data, isFetching, isError } = useFinanceiroLancamentos({
    ...baseParams,
    status,
    order: base === 'caixa' ? 'data_pagamento' : 'data_vencimento',
    order_by: 'desc',
    page,
    size: PAGE_SIZE,
  });

  const aplicarStatus = (novo?: string) => {
    setStatus(prev => (prev === novo ? undefined : novo));
    resetPage();
  };

  const temFiltro = !!(status || categoria || centro || f.q);
  const limparFiltros = () => {
    setStatus(undefined);
    setCategoria(undefined);
    setCentro(undefined);
    if (f.q) f.setQ('');
    resetPage();
  };

  const itensLanc = data?.items ?? [];
  const pagination = {
    page: data?.page ?? page,
    pages: data?.pages ?? 1,
    size: data?.size ?? PAGE_SIZE,
    total: data?.total ?? 0,
    next_page: data?.next_page ?? null,
    previous_page: data?.previous_page ?? null,
  };

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <FinanceiroPageHeader
          eyebrow={HEADER[tipo].eyebrow}
          titulo={t.titulo}
          subtitulo={t.subtitulo}
          icon={HEADER[tipo].icon}
          cor={acc}
          actions={
            <Flex align="center" gap={2}>
              <Text
                fontSize="11px"
                fontWeight={700}
                color="#94A3B8"
                textTransform="uppercase"
                letterSpacing="0.06em"
                display={{ base: 'none', sm: 'block' }}
              >
                Data por
              </Text>
              <Segmented
                value={base}
                onChange={v => {
                  setBase(v as 'vencimento' | 'competencia' | 'caixa');
                  resetPage();
                }}
                accent={acc}
                options={[
                  { value: 'vencimento', label: 'Vencimento' },
                  { value: 'competencia', label: 'Competência' },
                  { value: 'caixa', label: 'Caixa' },
                ]}
              />
            </Flex>
          }
          info={
            <Text>
              {tipo === 'RECEITA'
                ? 'Honorários e recebíveis do escritório. Use os filtros para recortar por status, categoria ou centro de custo; clique nos KPIs para filtrar por status.'
                : 'Despesas a pagar do escritório. Use os filtros para recortar por status, categoria ou centro de custo; clique nos KPIs para filtrar por status.'}
            </Text>
          }
        />

        {/* KPIs (filtram por status onde o rótulo é certo) */}
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={5}>
          <KpiCard
            label={t.totalKpi}
            valor={kpis?.total.valor ?? 0}
            qtd={kpis?.total.qtd}
            color="#1B88F5"
            icon={<Receipt size={20} weight="duotone" />}
            active={status === undefined}
            onClick={() => aplicarStatus(undefined)}
            index={0}
            info={
              <InfoPopover title={t.totalKpi}>
                Soma de <b>todos os títulos</b> (campo <i>total</i>) no período, franquia e filtros
                atuais. Clique no card para limpar o filtro de status.
              </InfoPopover>
            }
          />
          <KpiCard
            label={t.pago}
            valor={kpis?.pago.valor ?? 0}
            qtd={kpis?.pago.qtd}
            color="#16A34A"
            icon={<CheckCircle size={20} weight="duotone" />}
            index={1}
            info={
              <InfoPopover title={t.pago}>
                Soma do que já foi {tipo === 'RECEITA' ? 'recebido' : 'pago'} (campo <i>pago</i>). A
                quantidade conta apenas títulos com pagamento maior que zero.
              </InfoPopover>
            }
          />
          <KpiCard
            label="Em Aberto"
            valor={kpis?.em_aberto.valor ?? 0}
            qtd={kpis?.em_aberto.qtd}
            color="#1B88F5"
            icon={<Clock size={20} weight="duotone" />}
            index={2}
            info={
              <InfoPopover title="Em Aberto">
                Saldo ainda não pago (campo <i>nao_pago</i>) de títulos com saldo que{' '}
                <b>não estão vencidos nem perdidos</b>.
              </InfoPopover>
            }
          />
          <KpiCard
            label="Vencido"
            valor={kpis?.vencido.valor ?? 0}
            qtd={kpis?.vencido.qtd}
            color="#D97706"
            icon={<WarningCircle size={20} weight="duotone" />}
            active={status === 'ATRASADO'}
            onClick={() => aplicarStatus('ATRASADO')}
            index={3}
            info={
              <InfoPopover title="Vencido">
                Saldo não pago (<i>nao_pago</i>) de títulos com status <b>Vencido</b> (ATRASADO).
                Clique para filtrar a lista só pelos vencidos.
              </InfoPopover>
            }
          />
        </SimpleGrid>

        {/* Barra de filtros: busca + categoria + centro + status pills */}
        <GlassCard intensity="medium" p={{ base: 3, md: 4 }} mb={4}>
          <Flex gap={3} wrap="wrap" align="center">
            <BuscaInput
              value={f.q}
              onChange={v => {
                f.setQ(v);
                resetPage();
              }}
              placeholder={`Buscar ${t.pessoa.toLowerCase()} ou descrição...`}
              accent={acc}
            />
            <Box minW="190px" flex={{ base: '1 1 100%', md: '0 1 220px' }}>
              <Select<Opt>
                isClearable
                placeholder="Categoria"
                options={catOptions}
                value={categoria ? { value: categoria, label: categoria } : null}
                onChange={opt => {
                  setCategoria(opt?.value);
                  resetPage();
                }}
                size="sm"
              />
            </Box>
            <Box minW="170px" flex={{ base: '1 1 100%', md: '0 1 200px' }}>
              <Select<Opt>
                isClearable
                placeholder="Centro de custo"
                options={centroOptions}
                value={centro ? { value: centro, label: centro } : null}
                onChange={opt => {
                  setCentro(opt?.value);
                  resetPage();
                }}
                size="sm"
              />
            </Box>
            {temFiltro && (
              <Button
                size="sm"
                variant="ghost"
                color="#64748B"
                leftIcon={<X size={13} />}
                onClick={limparFiltros}
              >
                Limpar
              </Button>
            )}
          </Flex>

          <Flex gap={2} mt={3} wrap="wrap" align="center">
            <Flex align="center" gap={1.5} color="#94A3B8" mr={1}>
              <FunnelSimple size={14} />
              <Text
                fontSize="11px"
                fontWeight={700}
                textTransform="uppercase"
                letterSpacing="0.05em"
              >
                Status
              </Text>
            </Flex>
            <StatusPill ativo={!status} cor="#64748B" onClick={() => aplicarStatus(undefined)}>
              Todos
            </StatusPill>
            {statusPills.map(s => (
              <StatusPill
                key={s}
                ativo={status === s}
                cor={STATUS_META[s].cor}
                onClick={() => aplicarStatus(s)}
              >
                {STATUS_META[s].label(tipo)}
              </StatusPill>
            ))}
          </Flex>
        </GlassCard>

        {/* Lista de lançamentos (cards) */}
        <GlassCard intensity="strong" p={{ base: 2, md: 3 }}>
          {isError && (
            <Flex direction="column" align="center" gap={3} py={10}>
              <XCircle size={40} color="#DC2626" weight="duotone" />
              <Text color="#71717A">Não foi possível carregar os lançamentos.</Text>
            </Flex>
          )}
          {!isError && itensLanc.length === 0 && (
            <Flex direction="column" align="center" gap={2} py={12}>
              <Text color="#94A3B8" fontSize="sm">
                Nenhum lançamento para os filtros selecionados.
              </Text>
            </Flex>
          )}
          {!isError && itensLanc.length > 0 && (
            <>
              <Flex direction="column" opacity={isFetching ? 0.55 : 1} transition="opacity 0.15s">
                {itensLanc.map(item => (
                  <LancamentoCard key={item.id} item={item} />
                ))}
              </Flex>
              <Flex
                justify="space-between"
                align="center"
                px={2}
                pt={3}
                mt={1}
                borderTop="1px solid rgba(15,23,42,0.06)"
              >
                <Text fontSize="xs" color="#94A3B8">
                  {pagination.total} lançamento{pagination.total === 1 ? '' : 's'}
                </Text>
                <Flex align="center" gap={1}>
                  <Button
                    size="xs"
                    variant="ghost"
                    color="#475569"
                    leftIcon={<CaretLeft size={13} />}
                    isDisabled={!pagination.previous_page}
                    onClick={() => irParaPagina(page - 1)}
                  >
                    Anterior
                  </Button>
                  <Text fontSize="xs" color="#64748B" fontWeight={600} px={2}>
                    {pagination.page} / {pagination.pages}
                  </Text>
                  <Button
                    size="xs"
                    variant="ghost"
                    color="#475569"
                    rightIcon={<CaretRight size={13} />}
                    isDisabled={!pagination.next_page}
                    onClick={() => irParaPagina(page + 1)}
                  >
                    Próxima
                  </Button>
                </Flex>
              </Flex>
            </>
          )}
        </GlassCard>
      </Box>
    </GlassPageBg>
  );
};

const LancamentoCard = ({ item }: { item: ILancamentoFinanceiro }) => {
  const venc = isVencido(item.status_traduzido);
  const dias = venc ? diasAtraso(item.data_vencimento) : 0;
  const pago = item.pago || 0;
  return (
    <Flex
      direction={{ base: 'column', sm: 'row' }}
      align={{ base: 'stretch', sm: 'center' }}
      gap={{ base: 2, sm: 4 }}
      px={{ base: 2, md: 3 }}
      py={3}
      borderLeft="3px solid"
      borderLeftColor={venc ? '#D97706' : 'transparent'}
      _notLast={{ borderBottom: '1px solid rgba(15,23,42,0.05)' }}
      _hover={{ bg: 'rgba(15,23,42,0.02)' }}
      transition="background 0.15s"
    >
      {/* Identidade + descrição + datas */}
      <Box flex="1" minW={0}>
        <Flex align="center" gap={2} mb={0.5} wrap="wrap">
          <Text fontWeight={700} color="#0F172A" fontSize="sm" noOfLines={1}>
            {item.pessoa_nome || '—'}
          </Text>
          {item.categoria && (
            <Box
              fontSize="10.5px"
              fontWeight={600}
              color="#64748B"
              bg="rgba(15,23,42,0.05)"
              px={1.5}
              py="1px"
              borderRadius="full"
              noOfLines={1}
              maxW="200px"
            >
              {item.categoria}
            </Box>
          )}
        </Flex>
        {item.descricao && (
          <Text fontSize="13px" color="#475569" noOfLines={2} mb={1.5}>
            {item.descricao}
          </Text>
        )}
        <Flex align="center" gap={1.5} fontSize="11px" color="#94A3B8" wrap="wrap">
          <Box as="span">
            Venc{' '}
            <Box as="span" color={venc ? '#DC2626' : '#475569'} fontWeight={venc ? 700 : 600}>
              {fmtData(item.data_vencimento)}
            </Box>
          </Box>
          {venc && dias > 0 && (
            <Badge
              fontSize="9px"
              px={1}
              borderRadius="sm"
              bg="#FEF3C7"
              color="#B45309"
              textTransform="none"
            >
              +{dias}d
            </Badge>
          )}
          <Box as="span">· Comp {fmtCompetencia(item.data_competencia)}</Box>
          {item.data_pagamento && (
            <Box as="span">
              · Pago{' '}
              <Box as="span" color="#059669" fontWeight={700}>
                {fmtData(item.data_pagamento)}
              </Box>
            </Box>
          )}
        </Flex>
      </Box>

      {/* Valores + status */}
      <Flex
        direction={{ base: 'row', sm: 'column' }}
        align={{ base: 'center', sm: 'flex-end' }}
        justify="space-between"
        gap={1.5}
        flexShrink={0}
        minW={{ sm: '150px' }}
      >
        <Box textAlign={{ base: 'left', sm: 'right' }}>
          <Text
            fontSize="16px"
            fontWeight={800}
            color={venc ? '#D97706' : '#0F172A'}
            className="tabular-nums"
            lineHeight={1.15}
          >
            {brl(item.nao_pago)}
          </Text>
          <Text fontSize="11px" color="#94A3B8" className="tabular-nums">
            de {brl(item.total)}
            {pago > 0 ? ` · pago ${brl(pago)}` : ''}
          </Text>
        </Box>
        <StatusBadge status={item.status_traduzido} />
      </Flex>
    </Flex>
  );
};

const StatusPill = ({
  ativo,
  cor,
  onClick,
  children,
}: {
  ativo: boolean;
  cor: string;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <Box
    as="button"
    onClick={onClick}
    px={2.5}
    py={1}
    borderRadius="full"
    fontSize="12px"
    fontWeight={600}
    borderWidth="1.5px"
    transition="all 0.15s"
    bg={ativo ? cor : 'transparent'}
    color={ativo ? 'white' : cor}
    borderColor={ativo ? cor : `${cor}55`}
    _hover={{ borderColor: cor, bg: ativo ? cor : `${cor}14` }}
  >
    {children}
  </Box>
);
